import {
  useState,
  useEffect,
  FormEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  ReactNode,
} from 'react'

import { clsx } from 'clsx'
import { FormProvider, get, useForm, useFormContext } from 'react-hook-form'
import type { FieldValues, DefaultValues, FieldPath } from 'react-hook-form'

import { isClientError } from '@tribeplatform/gql-client/lib'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import {
  UploadQueueProvider,
  useUploadQueue,
} from '../Providers/UploadQueueProvider.js'

import { FormRenderProps } from './typings.js'
import type { FormComponentType, FormProps } from './typings.js'
import { flattenFormSubmitErrors, getErrorFieldNames } from './utils.js'

const FormContent = ({
  children,
  submitForm,
}: {
  children:
    | ReactNode
    | ((renderProps: FormRenderProps<FieldValues>) => ReactNode)
  submitForm: (data: FieldValues) => Promise<void>
}) => {
  const methods = useFormContext()
  const { isUploading } = useUploadQueue()

  if (typeof children === 'function') {
    const childrenToRender = children({ ...methods, isUploading, submitForm })
    return <>{childrenToRender}</>
  }

  return <>{children}</>
}

const FormInner = <TFormValues extends FieldValues>(
  {
    defaultValues = {} as DefaultValues<TFormValues>,
    children,
    onSubmit,
    alertClasses,
    loading,
    className,
    ...rest
  }: FormProps<TFormValues>,
  ref,
) => {
  const methods = useForm<TFormValues>({
    defaultValues,
    // Focus is moved in an effect below, once the errors have rendered.
    shouldFocusError: false,
  })
  const formRef = useRef<HTMLFormElement>(null)
  const focusedSubmitCount = useRef(0)
  const lastSubmitInvalid = useRef(false)
  const [errorAnnouncement, setErrorAnnouncement] = useState('')
  const [generalErrors, setGeneralErrors] = useState([])
  const submittedData = useRef<DefaultValues<TFormValues>>()
  const submitFailed = useRef(false)

  const {
    setError,
    handleSubmit,
    reset,
    setFocus,
    control,
    formState: { isSubmitSuccessful, submitCount, errors },
  } = methods

  const submitForm = async data => {
    if (!onSubmit) {
      return
    }

    try {
      setGeneralErrors([])
      submitFailed.current = false
      submittedData.current = data
      await onSubmit(data, methods)
    } catch (err) {
      submitFailed.current = true

      if (!isClientError(err)) {
        setGeneralErrors(err?.message ? [err] : [])
        return
      }

      const flattenedErrors = flattenFormSubmitErrors(err)
      const fieldErrors = flattenedErrors.filter(e => !!e.field)
      const otherErrors = flattenedErrors.filter(e => !e.field && !!e.message)
      setGeneralErrors(otherErrors)

      fieldErrors.forEach(e => {
        setError(e.field as FieldPath<TFormValues>, {
          type: 'validate',
          message: e?.message,
        })
      })
    }
  }

  useEffect(() => {
    if (isSubmitSuccessful && submittedData.current && !submitFailed.current) {
      reset({ ...submittedData.current })
    }
  }, [isSubmitSuccessful, reset, submitCount])

  // Runs after the errors are committed, so the focused field already carries
  // aria-invalid and aria-describedby when screen readers announce it.
  useEffect(() => {
    if (submitCount === focusedSubmitCount.current) {
      return
    }
    focusedSubmitCount.current = submitCount
    if (!submitCount || isSubmitSuccessful) {
      lastSubmitInvalid.current = false
      setErrorAnnouncement('')
      return
    }

    const errorNames = getErrorFieldNames(errors)
    const form = formRef.current
    // Alerts only speak when inserted, so a repeat failure with unchanged errors
    // would be silent. The parity space makes the live region text change each time.
    if (lastSubmitInvalid.current && errorNames.length) {
      const firstAlert = form?.querySelector('[role="alert"]')
      setErrorAnnouncement(
        `${firstAlert?.textContent ?? ''}${submitCount % 2 ? '\u00A0' : ''}`,
      )
    } else if (!errorNames.length) {
      setErrorAnnouncement('')
    }
    lastSubmitInvalid.current = errorNames.length > 0

    // DOM order: react-hook-form's error order changes as fields are fixed and break again.
    // Marked controls cover custom widgets; registered refs and named elements cover
    // errored fields that carry no aria-invalid (e.g. checkboxes).
    const candidates = new Set<HTMLElement>(
      form?.querySelectorAll<HTMLElement>('[aria-invalid="true"]') ?? [],
    )
    const namedElements = Array.from(
      form?.querySelectorAll<HTMLElement>('[name]') ?? [],
    )
    errorNames.forEach(name => {
      const ref = get(control._fields, name)?._f?.ref
      if (ref instanceof HTMLElement) {
        candidates.add(ref)
      }
      namedElements
        .filter(element => element.getAttribute('name') === name)
        .forEach(element => candidates.add(element))
    })
    const ordered = Array.from(candidates)
      .filter(
        element =>
          form?.contains(element) &&
          !(element instanceof HTMLInputElement && element.type === 'hidden'),
      )
      .sort((a, b) =>
        // eslint-disable-next-line no-bitwise
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1,
      )
    const focused = ordered.find(element => {
      element.focus({ preventScroll: true })
      return document.activeElement === element
    })
    if (focused) {
      // A plain focus() leaves fields that are "in view" under the sticky header.
      focused.scrollIntoView({ block: 'center' })
      return
    }

    const [firstErrorName] = errorNames
    if (firstErrorName) {
      setFocus(firstErrorName as FieldPath<TFormValues>)
    }
  }, [control, errors, isSubmitSuccessful, setFocus, submitCount])

  useImperativeHandle(ref, () => ({
    methods,
  }))

  /**
   * Submit event still propagates to parent when using portal
   * https://reactjs.org/docs/portals.html#event-bubbling-through-portals
   * Event bubbling goes through React DOM instead of HTML DOM
   * Portals don't have an effect on this one, we need to stop event propagation
   * This should be our default form handling method
   */
  const handleSubmitWithoutPropagation = (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    handleSubmit(submitForm)(e)
  }

  return (
    <FormProvider {...methods}>
      <UploadQueueProvider>
        <form
          ref={formRef}
          className={clsx('relative', className)}
          onSubmit={handleSubmitWithoutPropagation}
          data-submit-successful={isSubmitSuccessful || undefined}
          {...rest}
        >
          {loading && (
            <div className="absolute cursor-not-allowed bg-background-overlay w-full h-full z-50 flex start-0 end-0 items-center justify-center rounded-base">
              <SvgIcon className="animate-spin" size="lg" name="spinner" />
            </div>
          )}

          {generalErrors?.length ? (
            <Alert status="error" className={alertClasses || 'mb-3'}>
              {generalErrors.map(err => {
                if (!err?.message) {
                  return null
                }
                return <div key={err?.message}>{err?.message}</div>
              })}
            </Alert>
          ) : null}

          <FormContent submitForm={submitForm}>{children}</FormContent>
          <div aria-live="polite" className="sr-only">
            {errorAnnouncement}
          </div>
        </form>
      </UploadQueueProvider>
    </FormProvider>
  )
}

export const Form = forwardRef(FormInner) as FormComponentType
